import { useState, useEffect, useCallback } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Save, AlertCircle, CheckCircle2, Building2, User, Loader2 } from 'lucide-react';
import { isValidBusinessEmail, EMAIL_ERROR_TR } from '@/lib/email';
import { TR_CITY_NAMES, getDistricts } from '@/data/tr-cities';

const onlyDigits = (s) => (s || '').replace(/\D/g, '');

export const BillingInfo = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    entity_type: 'company',
    trade_name: '',
    full_name: '',
    tax_number: '',
    tax_office: '',
    billing_city: '',
    billing_district: '',
    billing_address: '',
    billing_phone: '',
    billing_email: '',
  });
  const [existing, setExisting] = useState(false);

  const fetchBilling = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/business/billing`);
      if (data && data.entity_type) {
        setForm({
          entity_type: data.entity_type || 'company',
          trade_name: data.trade_name || '',
          full_name: data.full_name || '',
          tax_number: data.tax_number || '',
          tax_office: data.tax_office || '',
          billing_city: data.billing_city || '',
          billing_district: data.billing_district || '',
          billing_address: data.billing_address || '',
          billing_phone: data.billing_phone || '',
          billing_email: data.billing_email || '',
        });
        setExisting(true);
      }
    } catch {
      /* empty */
    }
    setLoading(false);
  }, [API_URL]);

  useEffect(() => { fetchBilling(); }, [fetchBilling]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Client validation
    if (form.entity_type === 'company' && !form.trade_name.trim()) {
      toast.error('Ticaret ünvanı zorunlu'); return;
    }
    if (form.entity_type === 'individual' && !form.full_name.trim()) {
      toast.error('Ad soyad zorunlu'); return;
    }
    const taxLen = form.tax_number.length;
    if (form.entity_type === 'company' && !(taxLen === 10 || taxLen === 11)) {
      toast.error('Şirket VKN 10 haneli olmalı'); return;
    }
    if (form.entity_type === 'individual' && taxLen !== 11) {
      toast.error('Şahıs TCKN 11 haneli olmalı'); return;
    }
    for (const [k, label] of [['tax_office', 'Vergi Dairesi'], ['billing_city', 'İl'], ['billing_district', 'İlçe'], ['billing_address', 'Adres'], ['billing_phone', 'Telefon'], ['billing_email', 'E-posta']]) {
      if (!form[k].trim()) { toast.error(`${label} zorunlu`); return; }
    }
    if (!isValidBusinessEmail(form.billing_email)) { toast.error(EMAIL_ERROR_TR); return; }

    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.entity_type === 'company') payload.full_name = null;
      if (payload.entity_type === 'individual') payload.trade_name = null;
      await axios.put(`${API_URL}/api/business/billing`, payload);
      toast.success('Fatura bilgileri kaydedildi');
      setExisting(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    }
    setSaving(false);
  };

  if (loading) {
    return <PageLayout><div className="text-center py-16 text-[#595959]">Yükleniyor...</div></PageLayout>;
  }

  return (
    <PageLayout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2" data-testid="billing-title">
            <FileText className="w-7 h-7" /> Fatura Bilgileri
          </h1>
          <p className="text-base text-[#595959] mt-2">
            Abonelik faturalarınız için vergi ve iletişim bilgileriniz. <strong className="text-[#DC2626]">Zorunlu alanlar</strong>.
          </p>
        </div>

        {!existing && (
          <div className="mb-5 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="billing-not-complete-banner">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Fatura bilgileriniz eksik</p>
              <p className="text-xs mt-0.5">Aylık aboneliğinizin düzenlenebilmesi için bu bilgileri eksiksiz doldurmanız gerekiyor.</p>
            </div>
          </div>
        )}

        {existing && (
          <div className="mb-5 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800" data-testid="billing-complete-banner">
            <CheckCircle2 className="w-5 h-5 shrink-0" />
            <span>Fatura bilgileriniz kayıtlı. Değişiklik yapmak için aşağıdaki alanları güncelleyebilirsiniz.</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="billing-form">
          {/* Entity type selector */}
          <Card className="border border-[#E5E5E5]">
            <CardContent className="p-5">
              <Label className="mb-3 block font-semibold">İşletme Tipi <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-2 gap-3">
                <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${form.entity_type === 'company' ? 'border-[#2563EB] bg-[#2563EB]/5' : 'border-[#E5E5E5] hover:border-[#595959]'}`}>
                  <input type="radio" name="entity_type" value="company" checked={form.entity_type === 'company'} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} className="hidden" data-testid="entity-type-company" />
                  <Building2 className="w-5 h-5 text-[#2563EB]" />
                  <div>
                    <p className="text-sm font-semibold">Şirket</p>
                    <p className="text-xs text-[#595959]">Anonim, Limited, vb.</p>
                  </div>
                </label>
                <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${form.entity_type === 'individual' ? 'border-[#2563EB] bg-[#2563EB]/5' : 'border-[#E5E5E5] hover:border-[#595959]'}`}>
                  <input type="radio" name="entity_type" value="individual" checked={form.entity_type === 'individual'} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} className="hidden" data-testid="entity-type-individual" />
                  <User className="w-5 h-5 text-[#2563EB]" />
                  <div>
                    <p className="text-sm font-semibold">Şahıs İşletmesi</p>
                    <p className="text-xs text-[#595959]">Bireysel / esnaf</p>
                  </div>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Tax info */}
          <Card className="border border-[#E5E5E5]">
            <CardContent className="p-5 space-y-4">
              <h2 className="font-semibold text-[#1A1A2E]">Vergi Bilgileri</h2>
              {form.entity_type === 'company' ? (
                <div>
                  <Label>Ticaret Ünvanı <span className="text-red-500">*</span></Label>
                  <Input value={form.trade_name} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} placeholder="Örn: Yıldız Yazılım A.Ş." required data-testid="trade-name-input" />
                </div>
              ) : (
                <div>
                  <Label>Ad Soyad <span className="text-red-500">*</span></Label>
                  <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Örn: Ahmet Yılmaz" required data-testid="full-name-input" />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>
                    {form.entity_type === 'company' ? 'Vergi Numarası (VKN)' : 'T.C. Kimlik Numarası'}{' '}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    inputMode="numeric"
                    value={form.tax_number}
                    onChange={(e) => setForm({ ...form, tax_number: onlyDigits(e.target.value).slice(0, 11) })}
                    placeholder={form.entity_type === 'company' ? '10 haneli VKN' : '11 haneli TCKN'}
                    required
                    data-testid="tax-number-input"
                  />
                </div>
                <div>
                  <Label>Vergi Dairesi <span className="text-red-500">*</span></Label>
                  <Input value={form.tax_office} onChange={(e) => setForm({ ...form, tax_office: e.target.value })} placeholder="Örn: Beşiktaş" required data-testid="tax-office-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="border border-[#E5E5E5]">
            <CardContent className="p-5 space-y-4">
              <h2 className="font-semibold text-[#1A1A2E]">Adres Bilgileri</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>İl <span className="text-red-500">*</span></Label>
                  <Select
                    value={form.billing_city || undefined}
                    onValueChange={(v) => setForm({ ...form, billing_city: v, billing_district: '' })}
                  >
                    <SelectTrigger data-testid="billing-city-select">
                      <SelectValue placeholder="İl seçin" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {TR_CITY_NAMES.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`billing-city-opt-${c}`}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>İlçe <span className="text-red-500">*</span></Label>
                  <Select
                    value={form.billing_district || undefined}
                    onValueChange={(v) => setForm({ ...form, billing_district: v })}
                    disabled={!form.billing_city}
                  >
                    <SelectTrigger data-testid="billing-district-select">
                      <SelectValue placeholder={form.billing_city ? 'İlçe seçin' : 'Önce il seçin'} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {getDistricts(form.billing_city).map((d) => (
                        <SelectItem key={d} value={d} data-testid={`billing-district-opt-${d}`}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Açık Adres <span className="text-red-500">*</span></Label>
                <Textarea value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} placeholder="Mahalle, cadde, numara, daire..." rows={3} required data-testid="billing-address-input" />
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="border border-[#E5E5E5]">
            <CardContent className="p-5 space-y-4">
              <h2 className="font-semibold text-[#1A1A2E]">İletişim</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Telefon <span className="text-red-500">*</span></Label>
                  <Input value={form.billing_phone} onChange={(e) => setForm({ ...form, billing_phone: e.target.value })} placeholder="05XX XXX XX XX" required data-testid="billing-phone-input" />
                </div>
                <div>
                  <Label>E-posta (fatura) <span className="text-red-500">*</span></Label>
                  <Input type="email" value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} placeholder="fatura@isletme.com" required data-testid="billing-email-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Button type="submit" disabled={saving} className="w-full h-12 bg-[#2563EB] hover:bg-[#1E4ED8] text-white gap-2" data-testid="billing-save-btn">
            {saving ? <><Loader2 className="w-5 h-5 animate-spin" /> Kaydediliyor...</> : <><Save className="w-5 h-5" /> Kaydet</>}
          </Button>
        </form>
      </div>
    </PageLayout>
  );
};
